import { visualIndicatorsRule } from '@wix/a11y-rules';
import { useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus } from '@wix/rules-panel-ui';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { BiCtaNames } from '../../bi/types';
import { convertScanStatusToUiStatus } from '../../components/fixers/alt-image-fixer/utils';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { SITE_LEVEL_PAGE_ID } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { PanoramaErrorNames, PanoramaTransactions } from '../../panorama/constants';
import { usePanoramaFunctions } from '../../panorama/use-panorama-functions';
import { getFixerDesignProps, getFooterButtonTypes } from '../../utils/get-fixer-design-props';
import { useRuleBase } from '../shared/use-rule-base';
const ruleChecker = visualIndicatorsRule.checker;
export const useVisualIndicatorsRule = () => {
  const {
    getRuleState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    setVisualFocus
  } = a11yWizardAdapter;
  const {
    finishPanoramaTransaction,
    startPanoramaTransaction,
    reportErrorPanorama
  } = usePanoramaFunctions();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const getVisualIndicatorsProps = useCallback(_ref => {
    var _ruleState$state;
    let {
      ruleExecutionResult
    } = _ref;
    const {
      result,
      ruleId
    } = ruleExecutionResult;
    const {
      status
    } = result;
    const ruleState = getRuleState(SITE_LEVEL_PAGE_ID, ruleId);
    const uiStatus = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.status) ?? convertScanStatusToUiStatus(status);
    return {
      dataHook: `${SITE_LEVEL_PAGE_ID}-${ruleId}`,
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: uiStatus !== RuleListStatus.failed ? 'accessibility-wizard.rules-list.visual-indicators.description' : 'accessibility-wizard.rules-list.visual-indicators.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-focus-indicators-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: 1,
            failedElementCount: Number(status === 'failed')
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.visual-indicators.learn-more.aria-label')
      }),
      fixer: FixerType.ToggleRule,
      fixerProps: {
        completedRuleContent: {
          title: t('accessibility-wizard.rules-list.issue-resolved')
        },
        design: getFixerDesignProps(editorHost),
        footerLabels: {
          buttonTypes: getFooterButtonTypes(editorHost),
          dismiss: t('accessibility-wizard.rules-list.visual-indicators.footer-button.cancel'),
          submit: t('accessibility-wizard.rules-list.visual-indicators.footer-button.apply')
        },
        onApply: async checked => {
          startPanoramaTransaction(PanoramaTransactions.setFocusIndicators);
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Apply,
            index: 0,
            status: convertScanStatusToUiStatus(status)
          });
          biLogger.taskElementApplyClick({
            index: 0,
            status: convertScanStatusToUiStatus(status),
            value: checked.toString()
          });
          try {
            setVisualFocus(true);
          } catch (e) {
            reportErrorPanorama(e, PanoramaErrorNames.setFocusIndicators);
            return;
          }
          updateRuleStateAndVerifyIfNeeded(SITE_LEVEL_PAGE_ID, ruleId, {
            disabled: true,
            status: convertScanStatusToUiStatus(ruleChecker({
              visualFocusDisabled: false
            }).status)
          });
          setIsApplyTriggered(true);
          finishPanoramaTransaction(PanoramaTransactions.setFocusIndicators);
        },
        onCancel: () => {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Cancel,
            index: 0,
            status: uiStatus
          });
        },
        onSelect: selectedValue => {
          biLogger.elementFieldChange({
            index: 0,
            status: uiStatus,
            value: selectedValue.toString()
          });
        },
        toggleLabel: t('accessibility-wizard.rules-list.visual-indicators.toggle-label')
      },
      id: ruleId,
      isEditableOnceCompleted: false,
      label: t('accessibility-wizard.rules-list.visual-indicators.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          status: uiStatus
        });
        biLogger.elementStatusChange({
          index: 0,
          status: uiStatus
        });
      },
      parentGroupId: SITE_LEVEL_PAGE_ID,
      ruleId,
      status: uiStatus
    };
  }, [getRuleState, t, openSelectedArticle, editorHost, biLogger, startPanoramaTransaction, updateRuleStateAndVerifyIfNeeded, setIsApplyTriggered, finishPanoramaTransaction, setVisualFocus, reportErrorPanorama, setCurrentTaskInfo, currentTaskInfo]);
  return {
    getVisualIndicatorsProps
  };
};
//# sourceMappingURL=use-visual-indicators-rule.js.map